import axios from '@/axios';
import { AxiosInstance } from 'axios';
import {
  TSingleScanReportPayload,
  TSingleScanReport,
  TAppVersionReportPayload,
  TAppVersionReport,
  TSingleScanFoldersPayload,
  TAppVersionFoldersPayload,
  TFolder,
  TAppVersionPagePayload,
  TSingleScanPagePayload,
  TReportPageResponse,
  TDownloadPdfPayload,
  TDownloadPdfResponse,
  TComplianceSummary,
  TComplianceSummaryPayload,
  TComplianceRequirementListItem,
  TComplianceRequirementsListPayload,
  TComplianceFilterOptions,
  TComplianceMappingsResponse,
  TComplianceMappingsPayload,
} from '../models/types';

const axiosInstance = axios as AxiosInstance;

export async function fetchSingleScanReport(payload: TSingleScanReportPayload) {
  const { jobId, folderName } = payload;

  const response = await axiosInstance.get<TSingleScanReport>(`/private/ui/jobs/${jobId}`, {
    params: { folderName },
  });

  return response.data;
}

export async function fetchVersionReport(payload: TAppVersionReportPayload) {
  const { integrationId, branchName, versionId, folderName } = payload;

  const response = await axiosInstance.get<TAppVersionReport>(
    `/private/ui/integrations/${integrationId}/branches/${branchName}/versions/${versionId}`,
    {
      params: { folderName },
    }
  );

  return response.data;
}

export async function fetchSingleScanFolders(payload: TSingleScanFoldersPayload) {
  const { jobId, complianceRequirementId, complianceRequirementItemId } = payload;

  const response = await axiosInstance.get<TFolder[]>('/private/ui/folders', {
    params: { jobId, complianceRequirementId, complianceRequirementItemId },
  });

  return response.data;
}

export async function fetchAppVersionFolders(payload: TAppVersionFoldersPayload) {
  const { integrationId, versionId, complianceRequirementId, complianceRequirementItemId } =
    payload;

  const response = await axiosInstance.get<TFolder[]>('/private/ui/folders', {
    params: {
      integrationId,
      versionId,
      complianceRequirementId,
      complianceRequirementItemId,
    },
  });

  return response.data;
}

export async function fetchAppVersionPage(payload: TAppVersionPagePayload) {
  const { integrationId, branchName, versionId, folderName, page, limit, filters } = payload;

  const response = await axiosInstance.post<TReportPageResponse>(
    `/private/ui/integrations/${integrationId}/branches/${branchName}/versions/${versionId}/page`,
    {
      id: versionId,
      page,
      limit,
      integrationId,
      branchName,
      folderName,
      reqType: 'GET_PAGE',
      categories: { [versionId]: [] },
      filters,
    }
  );

  return response.data;
}

export async function downloadSingleScanPdf(
  payload: Extract<TDownloadPdfPayload, { isSingleScan: true }>
) {
  const { jobId, folderName, vulnerabilityCategoriesIds } = payload;
  const response = await axiosInstance.post<TDownloadPdfResponse>(
    `/private/ui/pdf_report/${jobId}`,
    {
      filters: { vulnerabilityCategoriesIds },
      folderName,
    }
  );
  return response.data;
}

export async function downloadAppVersionPdf(
  payload: Extract<TDownloadPdfPayload, { isSingleScan: false }>
) {
  const { integrationId, branchName, versionId, folderName, vulnerabilityCategoriesIds } = payload;
  const response = await axiosInstance.post<TDownloadPdfResponse>(
    `/private/ui/pdf_report/integrations/${integrationId}/branches/${branchName}/versions/${versionId}`,
    { filters: { vulnerabilityCategoriesIds }, folderName }
  );
  return response.data;
}

export async function updateSingleScanShared(jobId: string, shared: boolean) {
  const response = await axiosInstance.put(`/private/ui/jobs/${jobId}`, { shared });
  return response.data;
}

export async function fetchComplianceRequirementsList(payload: TComplianceRequirementsListPayload) {
  const { integrationId, search, jurisdiction, type, sector, enforcement } = payload;
  const response = await axiosInstance.get<TComplianceRequirementListItem[]>(
    `/private/ui/integrations/${integrationId}/compliance-requirements`,
    {
      params: {
        search: search || undefined,
        jurisdiction: jurisdiction?.length ? jurisdiction : undefined,
        type: type?.length ? type : undefined,
        sector: sector?.length ? sector : undefined,
        enforcement: enforcement?.length ? enforcement : undefined,
      },
    }
  );
  return response.data;
}

export async function fetchComplianceFilterOptions(integrationId: string) {
  const response = await axiosInstance.get<TComplianceFilterOptions>(
    `/private/ui/integrations/${integrationId}/compliance-requirements/filter-options`
  );
  return response.data;
}

export async function toggleComplianceRequirement(
  integrationId: string,
  requirementId: string,
  enabled: boolean
) {
  const response = await axiosInstance.put<{ success: boolean }>(
    `/private/ui/integrations/${integrationId}/compliance-requirements/${requirementId}`,
    { enabled }
  );
  return response.data;
}

export async function bulkToggleComplianceRequirements(
  integrationId: string,
  requirementIds: string[],
  enabled: boolean
) {
  const response = await axiosInstance.put<{ success: boolean }>(
    `/private/ui/integrations/${integrationId}/compliance-requirements/bulk`,
    { enabled, requirementIds }
  );
  return response.data;
}

export async function fetchComplianceSummary(payload: TComplianceSummaryPayload) {
  const { jobId, integrationId, folderName } = payload;
  const response = await axiosInstance.get<TComplianceSummary>(
    `/private/ui/jobs/${jobId}/compliance-summary`,
    { params: { integrationId, folderName } }
  );
  return response.data;
}

export async function fetchSingleScanPage(payload: TSingleScanPagePayload) {
  const { jobId, folderName, page, limit, filters } = payload;

  const response = await axiosInstance.post<TReportPageResponse>(`/private/ui/jobs/${jobId}/page`, {
    id: jobId,
    page,
    limit,
    folderName,
    reqType: 'GET_PAGE',
    categories: { [jobId]: [] },
    filters,
  });

  return response.data;
}

export async function fetchComplianceMappings(payload: TComplianceMappingsPayload) {
  const { vulnerabilityUid, integrationId } = payload;
  const response = await axiosInstance.get<TComplianceMappingsResponse>(
    `/private/ui/vulnerabilities/${vulnerabilityUid}/compliance-mappings`,
    { params: { integrationId } }
  );
  return response.data;
}
